import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, Package, ArrowLeft, Trash2, Music, 
  Copy, Edit
} from 'lucide-react';
import { API_URL } from '../utils/api';
import { MUSICAL_KEYS } from '../utils/musicalKeys';
import { isPackTempo } from '../utils/packTempo';

const MOOD_CATEGORIES = [
  "Calm", "Happy", "Sad", "Energetic", "Angry", "Confident", 
  "Reflective", "Hopeful", "Chill", "Dark", "Focused", "Warm", "Playful", "Tense"
];

const STYLE_TAGS = [
  "Trap", "R&B", "Afrobeats", "Latin", "Lo-Fi", "Boom Bap", "Drill", 
  "Pop", "Soul", "Jazz", "Electronic", "Acoustic", "Ambient", "Cinematic",
  "Hip Hop", "Rap", "Country", "Rock"
];

const USE_CASES = [
  "Class", "Therapy/SEL", "Event", "Seasonal", "Partner", "Curriculum", "Staff Picks", "Occasion"
];



const SoundPackList = () => {
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newPack, setNewPack] = useState({
    title: '',
    description: '',
    tempo_set: '',
    key: '',
    moods: [],
    styles: [],
    use_case: '',
    status: 'draft'
  });
  
  // Check auth
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/admin/login');
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate]);
  
  // Fetch packs
  useEffect(() => {
    fetchPacks();
  }, []);
  
  const fetchPacks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sound-packs`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPacks(data);
      }
    } catch (error) {
      console.error('Failed to fetch packs:', error);
      toast.error('Failed to load sound packs');
    } finally {
      setLoading(false);
    }
  };
  
  const createPack = async () => {
    if (!newPack.title.trim()) {
      toast.error('Pack title is required');
      return;
    }
    
    // Validate required fields
    if (!isPackTempo(newPack.tempo_set)) {
      toast.error('Enter a positive whole-number BPM.');
      return;
    }
    
    if (!newPack.key) {
      toast.error('Please select a musical key');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/sound-packs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newPack.title,
          description: newPack.description || null,
          tempo_set: parseInt(newPack.tempo_set),
          key: newPack.key,
          moods: newPack.moods,
          styles: newPack.styles,
          use_case: newPack.use_case || null,
          status: 'draft'
        })
      });
      
      if (response.status === 401) {
        toast.error('Session expired - please log in again');
        navigate('/admin/login');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Themed pack created');
        setShowCreate(false);
        setNewPack({ title: '', description: '', tempo_set: '', key: '', moods: [], styles: [], use_case: '', status: 'draft' });
        navigate(`/admin/sound-packs/${data.pack_id}`);
      } else {
        let errorMsg = 'Failed to create pack';
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch (e) {
          errorMsg = `Server error (${response.status})`;
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Create pack error:', error);
      toast.error('Network error - please try again');
    }
  };
  
  const clonePack = async (packId, packTitle) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sound-packs/${packId}/clone`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Cloned "${packTitle}"`);
        fetchPacks();
        navigate(`/admin/sound-packs/${data.new_pack_id}`);
      } else {
        toast.error('Failed to clone pack');
      }
    } catch (error) {
      toast.error('Error cloning pack');
    }
  };
  
  const deletePack = async (packId, packTitle) => {
    if (!window.confirm(`Delete "${packTitle}"? This cannot be undone.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/sound-packs/${packId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Pack deleted');
        fetchPacks();
      } else {
        toast.error('Failed to delete pack');
      }
    } catch (error) {
      toast.error('Error deleting pack');
    }
  };
  
  const toggleStatus = (mood) => {
    setNewPack(prev => ({
      ...prev,
      moods: prev.moods.includes(mood) 
        ? prev.moods.filter(m => m !== mood)
        : [...prev.moods, mood]
    }));
  };
  
  const toggleStyle = (style) => {
    setNewPack(prev => ({
      ...prev,
      styles: prev.styles.includes(style)
        ? prev.styles.filter(s => s !== style)
        : [...prev.styles, style]
    }));
  };
  
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
                <Package className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl">Themed Packs</h1>
                <p className="text-xs text-muted-foreground">Curate structured sound packs for classes & sessions</p>
              </div>
            </div>
          </div>
          
          <Button
            data-testid="create-pack-btn"
            onClick={() => setShowCreate(true)}
            className="rounded-full bg-primary hover:bg-primary/90 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Pack
          </Button>
        </div>
      </header>
      
      {/* Pack Sections */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {packs.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Themed Packs Yet</h2>
            <p className="text-muted-foreground mb-4">Create your first themed pack for classroom or therapy use.</p>
            <Button
              onClick={() => setShowCreate(true)}
              className="rounded-full bg-primary hover:bg-primary/90 text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Pack
            </Button>
          </div>
        ) : (
          <div className="space-y-10">
            {/* Published Packs Section */}
            {packs.filter(p => p.status === 'published').length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <h2 className="text-lg font-semibold">Published Packs</h2>
                  <Badge className="bg-green-500/20 text-green-400 border-0 text-xs">
                    {packs.filter(p => p.status === 'published').length}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {packs.filter(p => p.status === 'published').map(pack => {
                    const title = pack.title || pack.name || 'Untitled Pack';
                    const tempo = pack.tempo_set || pack.bpm;
                    // total_loops is counted in SQL over pack_items. Summing
                    // pack.melody_loop_ids and its three siblings, as this did,
                    // reads four arrays that exist only on the old Mongo
                    // document, so every pack showed "0 loops".
                    const groupCounts = pack.group_counts || {};
                    const loopCount = pack.total_loops
                      ?? Object.values(groupCounts).reduce((a, b) => a + (b || 0), 0);
                    
                    return (
                      <div
                        key={pack.pack_id}
                        className="group relative rounded-xl border border-green-500/20 bg-green-500/5 hover:bg-green-500/10 transition-all overflow-hidden"
                      >
                        {/* Cover Image - Smaller */}
                        <div className="aspect-[4/3] bg-gradient-to-br from-green-500/10 to-primary/10 relative overflow-hidden">
                          {pack.cover_image_url ? (
                            <img 
                              src={pack.cover_image_url} 
                              alt={title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-8 h-8 text-green-500/30" />
                            </div>
                          )}
                          <div className="absolute top-2 right-2">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          </div>
                        </div>
                        
                        {/* Content - Compact */}
                        <div className="p-3">
                          <h3 className="font-semibold text-sm truncate mb-1">{title}</h3>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                            {tempo && <span>{tempo} BPM</span>}
                            {pack.key && <span>• {pack.key}</span>}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                            <Music className="w-3 h-3" />
                            <span>{loopCount} loops</span>
                          </div>
                          
                          {/* Actions - Compact */}
                          <div className="flex items-center gap-1">
                            <Link to={`/admin/sound-packs/${pack.pack_id}`} className="flex-1">
                              <Button variant="ghost" size="sm" className="w-full h-7 text-xs rounded-lg">
                                <Edit className="w-3 h-3 mr-1" />
                                Edit
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => clonePack(pack.pack_id, title)}
                              className="h-7 w-7 p-0 rounded-lg"
                              title="Clone"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deletePack(pack.pack_id, title)}
                              className="h-7 w-7 p-0 rounded-lg text-destructive hover:text-destructive"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            
            {/* Draft Packs Section */}
            {packs.filter(p => p.status !== 'published').length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  <h2 className="text-lg font-semibold">Drafts</h2>
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-0 text-xs">
                    {packs.filter(p => p.status !== 'published').length}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {packs.filter(p => p.status !== 'published').map(pack => {
                    const title = pack.title || pack.name || 'Untitled Pack';
                    const tempo = pack.tempo_set || pack.bpm;
                    // total_loops is counted in SQL over pack_items. Summing
                    // pack.melody_loop_ids and its three siblings, as this did,
                    // reads four arrays that exist only on the old Mongo
                    // document, so every pack showed "0 loops".
                    const groupCounts = pack.group_counts || {};
                    const loopCount = pack.total_loops
                      ?? Object.values(groupCounts).reduce((a, b) => a + (b || 0), 0);
                    
                    return (
                      <Link
                        key={pack.pack_id}
                        to={`/admin/sound-packs/${pack.pack_id}`}
                        className="group relative rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all overflow-hidden"
                      >
                        {/* Small Cover */}
                        <div className="aspect-square bg-gradient-to-br from-yellow-500/10 to-secondary/10 relative overflow-hidden">
                          {pack.cover_image_url ? (
                            <img 
                              src={pack.cover_image_url} 
                              alt={title}
                              className="w-full h-full object-cover opacity-70"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-6 h-6 text-yellow-500/30" />
                            </div>
                          )}
                          <div className="absolute top-1.5 right-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                          </div>
                        </div>
                        
                        {/* Minimal Content */}
                        <div className="p-2">
                          <h3 className="font-medium text-xs truncate">{title}</h3>
                          <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                            {tempo && <span>{tempo}</span>}
                            <span>• {loopCount} loops</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
      
      {/* Create Pack Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Themed Pack</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Title *</label>
              <Input
                data-testid="pack-title-input"
                placeholder="e.g., Chill Classroom Vibes"
                value={newPack.title}
                onChange={(e) => setNewPack(prev => ({ ...prev, title: e.target.value }))}
                className="rounded-lg"
              />
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                placeholder="Short description (1-3 lines)"
                value={newPack.description}
                onChange={(e) => setNewPack(prev => ({ ...prev, description: e.target.value }))}
                className="rounded-lg resize-none"
                rows={2}
              />
            </div>
            
            {/* Tempo & Key */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tempo Set *</label>
                <Input type="number" min="1" step="1" inputMode="numeric" aria-label="Pack tempo (BPM)" data-testid="pack-tempo-input"
                        value={newPack.tempo_set}
                        onChange={event => setNewPack(prev => ({ ...prev, tempo_set: event.target.value }))}
                        placeholder="Exact BPM, e.g. 135" className="rounded-lg" />
                      <p className="text-xs text-muted-foreground mt-1">Enter the exact loop tempo, including 135 or 136.</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Key *</label>
                <Select
                  value={newPack.key}
                  onValueChange={(v) => setNewPack(prev => ({ ...prev, key: v }))}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Select Key" />
                  </SelectTrigger>
                  <SelectContent>
                    {MUSICAL_KEYS.map(key => (
                      <SelectItem key={key} value={key}>{key}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Use Case */}
            <div>
              <label className="block text-sm font-medium mb-1">Use Case</label>
              <Select
                value={newPack.use_case}
                onValueChange={(v) => setNewPack(prev => ({ ...prev, use_case: v }))}
              >
                <SelectTrigger className="rounded-lg">
                  <SelectValue placeholder="Select use case" />
                </SelectTrigger>
                <SelectContent>
                  {USE_CASES.map(uc => (
                    <SelectItem key={uc} value={uc}>{uc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Moods */}
            <div>
              <label className="block text-sm font-medium mb-2">Moods (optional)</label>
              <div className="flex flex-wrap gap-2">
                {MOOD_CATEGORIES.map(mood => (
                  <Button
                    key={mood}
                    size="sm"
                    variant={newPack.moods.includes(mood) ? 'default' : 'outline'}
                    onClick={() => toggleStatus(mood)}
                    className={`rounded-full text-xs ${newPack.moods.includes(mood) ? 'bg-primary text-black' : ''}`}
                  >
                    {mood}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Styles */}
            <div>
              <label className="block text-sm font-medium mb-2">Styles (optional)</label>
              <div className="flex flex-wrap gap-2">
                {STYLE_TAGS.map(style => (
                  <Button
                    key={style}
                    size="sm"
                    variant={newPack.styles.includes(style) ? 'default' : 'outline'}
                    onClick={() => toggleStyle(style)}
                    className={`rounded-full text-xs ${newPack.styles.includes(style) ? 'bg-secondary text-white' : ''}`}
                  >
                    {style}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowCreate(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              data-testid="create-pack-submit"
              onClick={createPack}
              disabled={!newPack.title.trim() || !newPack.tempo_set || !newPack.key}
              className="rounded-full bg-primary hover:bg-primary/90 text-black disabled:opacity-50"
            >
              Create Pack
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SoundPackList;
